import React, { useState } from 'react';
import CommonModal from '../../../Components/CommonModal';
import CommonLoader from '../../../Components/CommonLoader';
import { risk_portal_bu_create, risk_portal_bu_update } from '../../../services';

export const TIER_OPTIONS = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
];

// Normalise a BU code the same way the reference portal does live: uppercase,
// A–Z 0–9 + single hyphens, max 10 chars.
const normaliseCode = (v) => String(v || '')
    .trim().toUpperCase()
    .replace(/[\s_./\\]+/g, '-')
    .replace(/[^A-Z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .slice(0, 10);

// BusinessUnitForm — Add/Edit a business unit. CommonModal (config-driven
// fields). Create via risk_portal_bu_create, edit via risk_portal_bu_update.
// Mount fresh per open (parent passes a `key`) so defaultValues reflect the
// current `editing` record. Page owns the toast; this reports via callbacks.
//
// Props: show, onClose, onSuccess, onError, editing (bu|null), businessUnits.
export default function BusinessUnitForm({ show, onClose, onSuccess, onError, editing, businessUnits = [] }) {
    const [busy, setBusy] = useState(false);
    const isEdit = !!editing?.id;

    // Parent options — every other BU (a BU can't be its own parent).
    const parentOptions = [
        { value: '', label: '(Top-level)' },
        ...businessUnits
            .filter((b) => b.id !== editing?.id)
            .map((b) => ({ value: String(b.id), label: b.code ? `${b.name} (${b.code})` : b.name })),
    ];

    const submit = async (data) => {
        setBusy(true);
        try {
            const payload = {
                name: (data.name || '').trim(),
                code: normaliseCode(data.code),
                location: (data.location || '').trim(),
                risk_tier: data.risk_tier || 'medium',
                parent_id: data.parent_id ? parseInt(data.parent_id, 10) : null,
            };
            if (isEdit) await risk_portal_bu_update(editing.id, payload);
            else await risk_portal_bu_create(payload);
            onSuccess?.(isEdit ? 'Business unit updated' : 'Business unit created');
            onClose?.();
        } catch (e) {
            onError?.(e?.response?.data?.error?.message || e?.response?.data?.response_data?.message || 'Failed to save business unit');
        } finally {
            setBusy(false);
        }
    };

    return (
        <CommonModal
            show={show}
            onHide={onClose}
            mid
            title={isEdit ? 'Edit Business Unit' : 'Add Business Unit'}
            subtitle="Only Name is required — everything else is optional and can be filled in later."
            fields={[
                { name: 'name', label: 'Name', type: 'text', half: true, required: 'Name is required.' },
                { name: 'code', label: 'Code', type: 'text', half: true, placeholder: 'e.g. ENG, OPS-IN (auto from name if blank)' },
                { name: 'parent_id', label: 'Parent BU', type: 'select', half: true, options: parentOptions },
                { name: 'risk_tier', label: 'Risk Tier', type: 'select', half: true, options: TIER_OPTIONS },
                { name: 'location', label: 'Location', type: 'text', placeholder: 'e.g. Bangalore, EMEA' },
            ]}
            defaultValues={{
                name: editing?.name || '',
                code: editing?.code || '',
                parent_id: editing?.parent_id ? String(editing.parent_id) : '',
                risk_tier: editing?.risk_tier || 'medium',
                location: editing?.location || '',
            }}
            onSubmit={submit}
            buttons={[
                { text: 'Cancel', variant: 'secondary', onClick: onClose },
                { text: isEdit ? 'Save' : 'Create', variant: 'primary', type: 'submit' },
            ]}
        >
            {busy && <CommonLoader overlay label="Saving…" />}
        </CommonModal>
    );
}
